// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/enterprise-admin/organization/OrganizationPreferencesPanel.jsx

import React, { useEffect, useMemo, useState } from "react";
import { Select } from "../../../design-system/components";
import { TIMEZONE_OPTIONS } from "../shared/timezoneOptions";

const DATE_FORMAT_OPTIONS = [
  ["MM/DD/YYYY", "MM/DD/YYYY"],
  ["DD/MM/YYYY", "DD/MM/YYYY"],
  ["YYYY-MM-DD", "YYYY-MM-DD"],
];

const FRAMEWORK_OPTIONS = [
  ["", "No default framework"],
  ["soc2", "SOC 2"],
  ["nist_csf", "NIST CSF"],
  ["iso_27001", "ISO 27001"],
  ["fedramp", "FedRAMP"],
  ["hipaa", "HIPAA"],
  ["ccpa", "CCPA"],
];

const LANDING_PAGE_OPTIONS = [
  ["mission_control", "Mission Control"],
  ["mission_execution", "Mission Execution"],
  ["administration", "Administration"],
];

const RISK_VIEW_OPTIONS = [
  ["executive", "Executive"],
  ["operational", "Operational"],
  ["technical", "Technical"],
];

const SEVERITY_OPTIONS = [
  ["standard", "Standard"],
  ["business_impact", "Business impact"],
  ["audit_priority", "Audit priority"],
];

const DIGEST_OPTIONS = [
  ["off", "Off"],
  ["daily", "Daily"],
  ["weekly", "Weekly"],
];

function normalizePreferences(preferences) {
  const source = preferences || {};
  const businessHours = source.business_hours || {};
  const notifications = source.notification_defaults || {};

  return {
    timezone: source.timezone || "UTC",
    date_format: source.date_format || "YYYY-MM-DD",
    default_framework: source.default_framework || "",
    default_landing_page: source.default_landing_page || "mission_control",
    default_risk_view: source.default_risk_view || "executive",
    preferred_severity_terminology:
      source.preferred_severity_terminology || "standard",
    report_branding_name: source.report_branding_name || "",
    weekday_hours: businessHours.monday_friday || "09:00-17:00",
    administrative_digest: notifications.administrative_digest || "weekly",
  };
}

function serializeDraft(draft, preferences) {
  return {
    timezone: draft.timezone,
    date_format: draft.date_format,
    default_framework: draft.default_framework || null,
    default_landing_page: draft.default_landing_page,
    default_risk_view: draft.default_risk_view,
    preferred_severity_terminology: draft.preferred_severity_terminology,
    report_branding_name: draft.report_branding_name.trim() || null,
    business_hours: {
      ...(preferences?.business_hours || {}),
      monday_friday: draft.weekday_hours,
    },
    notification_defaults: {
      ...(preferences?.notification_defaults || {}),
      administrative_digest: draft.administrative_digest,
    },
  };
}

export default function OrganizationPreferencesPanel({
  preferences,
  onSave,
  saving,
}) {
  const initial = useMemo(
    () => normalizePreferences(preferences),
    [preferences]
  );
  const [draft, setDraft] = useState(initial);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    setDraft(initial);
    setNotice(null);
  }, [initial]);

  const isDirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(initial),
    [draft, initial]
  );

  const setField = (field, value) => {
    setDraft((current) => ({
      ...current,
      [field]: value,
    }));
    setNotice(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isDirty || saving) return;

    setNotice(null);
    try {
      await onSave(serializeDraft(draft, preferences));
      setNotice({
        type: "success",
        message: "Enterprise preferences saved and added to the administrative audit history.",
      });
    } catch (error) {
      setNotice({
        type: "error",
        message: String(error?.message || error),
      });
    }
  };

  return (
    <section className="enterprise-admin-section enterprise-admin-preferences-panel">
      <form className="enterprise-admin-form" onSubmit={handleSubmit}>
        <div className="enterprise-admin-section-header compact">
          <p className="enterprise-admin-eyebrow">Organization defaults</p>
          <h2>Preferences</h2>
          <p>
            Set the enterprise defaults used across Mission Control, reporting,
            and administrative views.
          </p>
        </div>

        <section className="enterprise-admin-preference-group">
          <div className="enterprise-admin-preference-group-heading">
            <div>
              <p className="enterprise-admin-eyebrow">Display & localization</p>
              <h3>Regional defaults</h3>
            </div>
            <p>Control how dates and operating context appear across OASYS.</p>
          </div>

          <div className="enterprise-admin-form-grid">
            <label>
              <span>Timezone</span>
              <Select
                value={draft.timezone}
                onChange={(event) => setField("timezone", event.target.value)}
              >
                {TIMEZONE_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Select>
            </label>

            <label>
              <span>Date format</span>
              <Select
                value={draft.date_format}
                onChange={(event) => setField("date_format", event.target.value)}
              >
                {DATE_FORMAT_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Select>
            </label>
          </div>
        </section>

        <section className="enterprise-admin-preference-group">
          <div className="enterprise-admin-preference-group-heading">
            <div>
              <p className="enterprise-admin-eyebrow">Operating defaults</p>
              <h3>Workspace behavior</h3>
            </div>
            <p>Choose the default views presented to authorized operators.</p>
          </div>

          <div className="enterprise-admin-form-grid">
            <label>
              <span>Default framework</span>
              <Select
                value={draft.default_framework}
                onChange={(event) =>
                  setField("default_framework", event.target.value)
                }
              >
                {FRAMEWORK_OPTIONS.map(([value, label]) => (
                  <option key={value || "none"} value={value}>{label}</option>
                ))}
              </Select>
            </label>

            <label>
              <span>Default landing page</span>
              <Select
                value={draft.default_landing_page}
                onChange={(event) =>
                  setField("default_landing_page", event.target.value)
                }
              >
                {LANDING_PAGE_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Select>
            </label>

            <label>
              <span>Default risk view</span>
              <Select
                value={draft.default_risk_view}
                onChange={(event) =>
                  setField("default_risk_view", event.target.value)
                }
              >
                {RISK_VIEW_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Select>
            </label>

            <label>
              <span>Severity terminology</span>
              <Select
                value={draft.preferred_severity_terminology}
                onChange={(event) =>
                  setField(
                    "preferred_severity_terminology",
                    event.target.value
                  )
                }
              >
                {SEVERITY_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Select>
            </label>

            <label className="enterprise-admin-preference-wide-field">
              <span>Report branding name</span>
              <input
                value={draft.report_branding_name}
                maxLength={160}
                placeholder="Organization name shown on generated reports"
                onChange={(event) =>
                  setField("report_branding_name", event.target.value)
                }
              />
            </label>
          </div>
        </section>

        <section className="enterprise-admin-preference-group">
          <div className="enterprise-admin-preference-group-heading">
            <div>
              <p className="enterprise-admin-eyebrow">Notifications & schedule</p>
              <h3>Administrative cadence</h3>
            </div>
            <p>
              Store business-hour and digest defaults without enabling autonomous
              actions.
            </p>
          </div>

          <div className="enterprise-admin-form-grid">
            <label>
              <span>Monday–Friday business hours</span>
              <input
                value={draft.weekday_hours}
                maxLength={40}
                placeholder="09:00-17:00"
                onChange={(event) =>
                  setField("weekday_hours", event.target.value)
                }
              />
              <small>Use the organization timezone selected above.</small>
            </label>

            <label>
              <span>Administrative digest</span>
              <Select
                value={draft.administrative_digest}
                onChange={(event) =>
                  setField("administrative_digest", event.target.value)
                }
              >
                {DIGEST_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Select>
              <small>This records the preferred cadence only.</small>
            </label>
          </div>
        </section>

        {notice ? (
          <div
            className={`enterprise-admin-preference-notice ${notice.type}`}
            role={notice.type === "error" ? "alert" : "status"}
          >
            {notice.message}
          </div>
        ) : null}

        <div className="enterprise-admin-action-row enterprise-admin-preference-actions">
          <button type="submit" disabled={saving || !isDirty}>
            {saving ? "Saving…" : isDirty ? "Save preferences" : "Preferences saved"}
          </button>
          <span>
            Preference changes create administrative audit events. They do not
            automatically authorize connectors or change provider access.
          </span>
        </div>
      </form>
    </section>
  );
}
